// src/pages/ArticleDetailPage/ArticleDetailPage.tsx

import { useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import ReactMarkdown from 'react-markdown'
import { useArticleBySlug } from '../../hooks/useArticle'
import { useAuth } from '../../hooks/useAuth'
import * as articleService from '../../services/articleService'
import styles from './ArticleDetailPage.module.css'

/* ─────────────────────────────────────────────────
   Icônes SVG inline
───────────────────────────────────────────────── */

const IconArrowLeft = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={styles.backIcon}>
    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
)

const IconCalendar = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={styles.metaIcon}>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
)

const IconUser = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={styles.metaIcon}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
)

const IconEdit = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={styles.adminIcon}>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
)

const IconTrash = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={styles.adminIcon}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
)

const IconPublish = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={styles.adminIcon}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
)

const IconUnpublish = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={styles.adminIcon}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="9.5" y1="9.5" x2="14.5" y2="14.5" />
    <line x1="14.5" y1="9.5" x2="9.5" y2="14.5" />
  </svg>
)

/* ─────────────────────────────────────────────────
   Skeleton article detail
───────────────────────────────────────────────── */

function SkeletonDetail() {
  return (
    <article className={styles.article} aria-hidden="true">
      <div className={`${styles.sk} ${styles.skCategory}`} />
      <div className={`${styles.sk} ${styles.skTitle}`} />
      <div className={`${styles.sk} ${styles.skTitleShort}`} />
      <div className={styles.skMeta}>
        <div className={`${styles.sk} ${styles.skMetaItem}`} />
        <div className={`${styles.sk} ${styles.skMetaItem}`} />
      </div>
      <div className={styles.skDivider} />
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className={`${styles.sk} ${styles.skLine}`}
          style={{ width: i % 3 === 2 ? '65%' : '100%' }} />
      ))}
    </article>
  )
}

/* ─────────────────────────────────────────────────
   Formatage de date
───────────────────────────────────────────────── */

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric',
  })
}

/* ─────────────────────────────────────────────────
   Composant principal
───────────────────────────────────────────────── */

export function ArticleDetailPage() {
  /*
   * L'URL est au format /blog/<slug>.
   * On récupère le paramètre slug directement.
   */
  const { slug = '' } = useParams<{ slug: string }>()

  // Auth en premier pour transmettre le token au hook (accès admin aux non publiés)
  const { user, token } = useAuth()
  const navigate = useNavigate()

  const isAdmin = user?.roles?.includes('Admin') ?? false

  const { article, loading, error, refetch } = useArticleBySlug(slug, isAdmin ? token : null)

  // État du bouton "Copy for LLMs"
  const [copied, setCopied] = useState(false)

  const handleCopyForLLM = async () => {
    if (!article) return
    const text = `# ${article.title}\n\n${article.content ?? ''}`
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch {
      /* clipboard non disponible */
    }
  }

  // Handlers pour les actions admin
  const handleEdit = () => {
    if (article?.id) {
      navigate(`/admin/edit-article/${article.id}`)
    }
  }

  const handleDelete = async () => {
    if (!token || !article?.id || !window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) {
      return
    }

    try {
      await articleService.deleteArticle(token, article.id)
      navigate('/blog')
    } catch (err) {
      alert('Erreur lors de la suppression de l\'article.')
    }
  }

  const handleTogglePublish = async () => {
    if (!token || !article) return

    try {
      await articleService.togglePublishArticle(token, article.id, !article.isPublished)
      refetch() // Recharger l'article pour mettre à jour l'état
    } catch (err) {
      alert('Erreur lors de la mise à jour du statut de publication.')
    }
  }

  /* ── État de chargement ── */
  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.inner}>
          <Link to="/blog" className={styles.back}>
            <IconArrowLeft /> Retour au blog
          </Link>
          <SkeletonDetail />
        </div>
      </div>
    )
  }

  /* ── État d'erreur ── */
  if (error || !article) {
    return (
      <div className={styles.page}>
        <div className={styles.inner}>
          <Link to="/blog" className={styles.back}>
            <IconArrowLeft /> Retour au blog
          </Link>
          <div className={styles.errorState} role="alert">
            <h1 className={styles.errorTitle}>Article introuvable</h1>
            <p className={styles.errorText}>{error ?? 'Cet article n\'existe pas ou a été supprimé.'}</p>
            <Link to="/blog" className={styles.retryBtn}>Voir tous les articles</Link>
          </div>
        </div>
      </div>
    )
  }

  /* ── Article chargé ── */
  return (
    <div className={styles.page}>
      <Helmet>
        <title>{`${article.title} — Steven YAMBOS`}</title>
        <meta name="description" content={article.description} />
        <meta property="og:title" content={`${article.title} — Steven YAMBOS`} />
        <meta property="og:description" content={article.description} />
        <meta property="og:url" content={`https://stevenyambos.fr/blog/${article.slug}`} />
        {article.cover && <meta property="og:image" content={article.cover} />}
        <meta property="og:type" content="article" />
        <meta property="article:author" content="Steven YAMBOS" />
        <meta property="article:published_time" content={article.publishedAt} />
      </Helmet>
      <div className={styles.bgGrid} aria-hidden="true" />

      <div className={styles.inner}>

        {/* ── Fil d'Ariane + retour ── */}
        <nav className={styles.breadcrumb} aria-label="Fil d'Ariane">
          <Link to="/" className={styles.breadcrumbLink}>Accueil</Link>
          <span className={styles.breadcrumbSep} aria-hidden="true">/</span>
          <Link to="/blog" className={styles.breadcrumbLink}>Blog</Link>
          <span className={styles.breadcrumbSep} aria-hidden="true">/</span>
          <span className={styles.breadcrumbCurrent}>{article.title}</span>
        </nav>

        <Link to="/blog" className={styles.back}>
          <IconArrowLeft />
          Retour au blog
        </Link>

        {/* ── Article ── */}
        <article className={styles.article}>

          {/* Badge catégorie */}
          <span className={styles.category}>{article.categories?.[0] || 'Général'}</span>

          {/* Titre */}
          <h1 className={styles.title}>{article.title}</h1>

          {/* Image de couverture */}
          {article.cover && (
            <div className={styles.coverContainer}>
              <img
                src={article.cover}
                alt={`Couverture de l'article ${article.title}`}
                className={styles.cover}
                loading="lazy"
              />
            </div>
          )}

          {/* Métadonnées */}
          <div className={styles.meta}>
            <span className={styles.metaItem}>
              <IconCalendar />
              <time dateTime={article.publishedAt}>
                {formatDate(article.publishedAt)}
              </time>
            </span>
            <span className={styles.metaDot} aria-hidden="true">·</span>
            <span className={styles.metaItem}>
              <IconUser />
              {'Steven YAMBOS'}
            </span>
          </div>

          {/* Tags */}
          {article.tags.length > 0 && (
            <ul className={styles.tags} aria-label="Tags">
              {article.tags.map((tag) => (
                <li key={tag} className={styles.tag}>
                  #{tag}
                </li>
              ))}
            </ul>
          )}

          {/* Séparateur */}
          <div className={styles.divider} aria-hidden="true" />

          {/* ── Bouton Copy for LLMs ── */}
          <div className={styles.copyLLMWrapper}>
            <button
              type="button"
              onClick={handleCopyForLLM}
              className={`${styles.copyLLMBtn} ${copied ? styles.copyLLMBtnCopied : ''}`}
              aria-label="Copier le titre et le contenu pour un LLM"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={styles.copyLLMIcon}>
                {copied
                  ? <><polyline points="20 6 9 17 4 12" /></>
                  : <>
                      <path d="M12 2l1.5 3.5L17 7l-3.5 1.5L12 12l-1.5-3.5L7 7l3.5-1.5Z" />
                      <path d="M19 14l.75 1.75L21.5 16.5l-1.75.75L19 19l-.75-1.75L16.5 16.5l1.75-.75Z" />
                    </>
                }
              </svg>
              {copied ? 'Copié !' : 'Copy for LLMs'}
            </button>
          </div>

          {/*
           * Contenu de l'article en Markdown.
           * Utilisation de ReactMarkdown pour le rendu.
           */}
          <div className={styles.content}>
            <ReactMarkdown>{article.content}</ReactMarkdown>
          </div>

        </article>

        {/* ── Contrôles admin ── */}
        {isAdmin && (
          <div className={styles.adminControls}>
            <h2 className={styles.adminTitle}>Administration</h2>
            <div className={styles.adminButtons}>
              <button
                type="button"
                onClick={handleEdit}
                className={`${styles.adminBtn} ${styles.editBtn}`}
                title="Modifier l'article"
              >
                <IconEdit />
                Modifier
              </button>
              <button
                type="button"
                onClick={handleTogglePublish}
                className={`${styles.adminBtn} ${article.isPublished ? styles.unpublishBtn : styles.publishBtn}`}
                title={article.isPublished ? 'Dépublier l\'article' : 'Publier l\'article'}
              >
                {article.isPublished ? <IconUnpublish /> : <IconPublish />}
                {article.isPublished ? 'Dépublier' : 'Publier'}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className={`${styles.adminBtn} ${styles.deleteBtn}`}
                title="Supprimer l'article"
              >
                <IconTrash />
                Supprimer
              </button>
            </div>
          </div>
        )}

        {/* ── Pied de page de l'article ── */}
        <div className={styles.articleFooter}>
          <Link to="/blog" className={styles.footerBack}>
            <IconArrowLeft />
            Voir d'autres articles
          </Link>
        </div>

      </div>
    </div>
  )
}